import { prepareExpDateField } from "./datePicker";
import { historyActionLabel } from "./historyLabels";
import { showToast } from "./toast";
import {
  afterWheelLedgerChange,
  readExpDateField,
  updateLedgerTransaction,
  WheelActionError,
  type WheelLedgerTransaction,
} from "./wheelActions";

const MODAL_ID = "sfe-history-txn-edit-modal";

function priceFieldLabel(txn: WheelLedgerTransaction): string {
  if (txn.instrument_type === "stock") return "Price per share";
  const action = (txn.action ?? "").toLowerCase();
  if (action === "buy") return "Premium paid per share";
  return "Premium per share";
}

function quantityFieldLabel(txn: WheelLedgerTransaction): string {
  return txn.instrument_type === "stock" ? "Shares" : "Contracts";
}

function modalTitle(txn: WheelLedgerTransaction): string {
  const code = historyActionLabel(txn);
  const ticker = txn.ticker ?? "—";
  if (txn.instrument_type === "stock") {
    return `Edit ${code} · ${ticker}`;
  }
  const side = (txn.side ?? "").toUpperCase();
  const strike = txn.strike != null ? `$${Number(txn.strike).toFixed(2)}` : "—";
  return `Edit ${code} · ${ticker} ${side} ${strike}`;
}

function closeModal(): void {
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
}

/**
 * Edit a ledger transaction in-place (Stock Forge+ modal → PUT /api/v1/wheel/transactions/:id).
 */
export function openHistoryTransactionEditModal(
  txn: WheelLedgerTransaction,
  options: { onSaved?: () => void | Promise<void> } = {},
): void {
  if (!(txn.id > 0)) {
    showToast("This row cannot be edited.", "error");
    return;
  }

  let overlay = document.getElementById(MODAL_ID);
  const isOption = txn.instrument_type === "option";
  const priceLabel = priceFieldLabel(txn);
  const qtyLabel = quantityFieldLabel(txn);

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-history-txn-edit-modal" role="dialog" aria-labelledby="sfe-history-txn-edit-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-history-txn-edit-title"></h3>
          <button type="button" class="sfe-modal-close" data-action="history-txn-edit-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-history-txn-edit-lead" data-history-txn-edit-lead></p>
        <form class="sfe-history-txn-edit-form" data-history-txn-edit-form>
          <label class="sfe-modal-field">Trade date
            <input type="date" name="trade_date" required />
          </label>
          <label class="sfe-modal-field"><span data-history-txn-qty-label>Contracts</span>
            <input type="number" name="quantity" min="1" step="1" required />
          </label>
          <label class="sfe-modal-field"><span data-history-txn-price-label>Premium per share</span>
            <input type="number" name="avg_price" min="0" step="0.01" required />
          </label>
          <div class="sfe-history-txn-edit-option-fields" data-history-txn-option-fields hidden>
            <label class="sfe-modal-field">Strike
              <input type="number" name="strike" min="0" step="0.01" />
            </label>
            <label class="sfe-modal-field">Expiration
              <input type="text" name="exp_date" class="sfe-focus-exp-input" placeholder="YYYY-MM-DD" inputmode="numeric" autocomplete="off" spellcheck="false" maxlength="10" />
            </label>
          </div>
          <div class="sfe-modal-actions sfe-history-txn-edit-actions">
            <button type="button" class="ghost small" data-action="history-txn-edit-cancel">Cancel</button>
            <button type="submit" class="small">Save changes</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        closeModal();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "history-txn-edit-close" || action === "history-txn-edit-cancel") {
        closeModal();
      }
    });
  }

  const title = overlay.querySelector("#sfe-history-txn-edit-title");
  const lead = overlay.querySelector("[data-history-txn-edit-lead]");
  const form = overlay.querySelector<HTMLFormElement>("[data-history-txn-edit-form]");
  const optionFields = overlay.querySelector<HTMLElement>("[data-history-txn-option-fields]");
  if (!form || !title || !lead || !optionFields) return;

  title.textContent = modalTitle(txn);
  lead.textContent = `Transaction #${txn.id}. Changes are saved to your Stock Forge ledger.`;
  overlay.querySelector("[data-history-txn-qty-label]")!.textContent = qtyLabel;
  overlay.querySelector("[data-history-txn-price-label]")!.textContent = priceLabel;

  optionFields.hidden = !isOption;
  const strikeInput = form.elements.namedItem("strike") as HTMLInputElement;
  const expInput = form.elements.namedItem("exp_date") as HTMLInputElement;
  strikeInput.required = isOption;
  expInput.required = isOption;

  (form.elements.namedItem("trade_date") as HTMLInputElement).value = txn.trade_date ?? "";
  (form.elements.namedItem("quantity") as HTMLInputElement).value = String(
    Math.abs(Number(txn.quantity) || 1),
  );
  (form.elements.namedItem("avg_price") as HTMLInputElement).value =
    txn.avg_price != null && Number.isFinite(txn.avg_price) ? String(txn.avg_price) : "";
  strikeInput.value = txn.strike != null && Number.isFinite(txn.strike) ? String(txn.strike) : "";
  prepareExpDateField(expInput, txn.exp_date ?? "", {
    onInteract: () => {
      /* keep modal open while picking expiration */
    },
  });

  form.onsubmit = (event) => {
    event.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');
    if (submit) submit.disabled = true;
    void (async () => {
      try {
        await updateLedgerTransaction(txn, {
          tradeDate: (form.elements.namedItem("trade_date") as HTMLInputElement).value,
          quantity: Number((form.elements.namedItem("quantity") as HTMLInputElement).value),
          avgPrice: Number((form.elements.namedItem("avg_price") as HTMLInputElement).value),
          strike: isOption ? Number(strikeInput.value) : undefined,
          expDate: isOption ? readExpDateField(expInput) : undefined,
        });
        showToast("Transaction updated.", "success");
        closeModal();
        await afterWheelLedgerChange(options.onSaved ?? undefined);
      } catch (err) {
        showToast(err instanceof WheelActionError ? err.message : "Could not update transaction.", "error");
      } finally {
        if (submit) submit.disabled = false;
      }
    })();
  };

  overlay.classList.add("sfe-modal-open");
  (form.elements.namedItem("avg_price") as HTMLInputElement).focus();
}
